import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';
import { vdjBeatChroma, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. La lumière est celle du rétroprojecteur.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'oilshow',
  family: 'shader',
  danceability: 0.70,
  requiresTier: 0,
  energyLevel: 0.60,
  bpmMin: 85,
  bpmMax: 135,
  movement: 0.65,
  tonality: 0.75,
  enabled: true
};

// OILSHOW : le light show à l'huile des concerts psychédéliques des années 60 :
// des encres et de l'huile entre deux plaques de verre sur un rétroprojecteur,
// et un projectionniste qui manipule la plaque en rythme. Rock psychédélique,
// funk, soul, disco, house : preset party de vdjLook à intensité modérée.
//
// LA SIMULATION (Stam, fluide stable) sur une grille de 256 x 144 (tier 2) :
// advection semi-lagrangienne de la vitesse, forces, divergence, pression par
// Jacobi (20 itérations), soustraction du gradient. La vitesse est en TEXELS
// par seconde (la grille a le format de l'écran, ses texels sont carrés), en
// demi-flottants quand une cible flottante est possible, sinon encodée en RGBA8
// (uEnc). Le Pi rend en flottant mais ne FILTRE pas : la vitesse se lit en
// bilinéaire manuel (4 texels). Les encres (six, deux cibles RGBA8 filtrées,
// 512 x 288) et l'HUILE (le canal alpha de la première) sont advectées sur la
// grille fine. L'huile est un champ qu'un terme de double puits ramène vers 0
// ou 1 à chaque pas (bords nets, R13) ; les encres sont expulsées de l'huile :
// elles sont lues en amont du gradient d'huile et effacées sous les bulles.
//
// LE RENDU, comme une diapositive projetée : fond blanc chaud, mélange
// SOUSTRACTIF des encres (chaque encre absorbe le complément de sa couleur,
// transmission exp(−k·densité)), zones vides lumineuses ; les bulles d'huile
// ont un liseré sombre et un reflet irisé du côté de la lampe ; vignettage
// rond de la lentille, aberration chromatique au bord, grain de projection.
// Les encres s'effacent lentement (demi-vie 40 s) : la plaque ne vire jamais
// à la boue.
//
// LE PROJECTIONNISTE : les forces suivent la musique comme une main :
//   kick (temps, ou attaque grave)  pression au centre, les couleurs s'écartent
//   mesure                          un coup de doigt : tourbillon quelque part
//   phrase                          une goutte versée (couleur suivante)
//   bands.mid                       rotation lente de toute la plaque
//   drop                            la plaque secouée une mesure, puis repos
// Intensité < 0,5 : seules la rotation et les gouttes ; > 0,7 : pression sur
// chaque temps, encres plus denses, séparation RGB légère. Scène (R20, 40 s) :
// deux gouttes versées au début ; la simulation est préchauffée (80 pas) avant
// la première image.

const TAU = Math.PI * 2;
const TIERS = {
  0: { w: 128, h: 72, dye: 2, iter: 12, oil: false },
  1: { w: 192, h: 108, dye: 2, iter: 16, oil: true },
  2: { w: 256, h: 144, dye: 2, iter: 20, oil: true }
};
const VMAX = 400;                      // texels/s, the RGBA8 encoding range
const SCENE_S = 40;
const NO_BPM = 112;
const INKS = [[0.95, 0.08, 0.12], [1.0, 0.45, 0.05], [0.95, 0.1, 0.65], [1.0, 0.9, 0.1], [0.1, 0.35, 1.0], [0.15, 0.85, 0.3]];   // red, orange, magenta, yellow, blue, green

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function hashStr(s) { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; } return h >>> 0; }

// --- GLSL ------------------------------------------------------------------------------
const quadVS = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
// velocity storage: half floats, or RGBA8 encoded around 0.5
const VEL_CHUNK = `
  uniform float uEnc;
  vec2 decV(vec4 t) { return uEnc > 0.5 ? (t.xy - 0.5) * ${(2 * VMAX).toFixed(1)} : t.xy; }
  vec4 encV(vec2 v) { return uEnc > 0.5 ? vec4(clamp(v / ${(2 * VMAX).toFixed(1)} + 0.5, 0.0, 1.0), 0.0, 1.0) : vec4(v, 0.0, 1.0); }
  float decS(vec4 t) { return uEnc > 0.5 ? (t.x - 0.5) * ${(2 * VMAX).toFixed(1)} : t.x; }
  vec4 encS(float s) { return uEnc > 0.5 ? vec4(clamp(s / ${(2 * VMAX).toFixed(1)} + 0.5, 0.0, 1.0), 0.0, 0.0, 1.0) : vec4(s, 0.0, 0.0, 1.0); }
  // the velocity at uv, bilinear by hand (float targets are not filtered on the Pi)
  vec2 velAt(sampler2D v, vec2 uv, vec2 texel) {
    vec2 p = uv / texel - 0.5;
    vec2 i = floor(p), f = p - i;
    vec2 a = decV(texture2D(v, (i + 0.5) * texel)), b = decV(texture2D(v, (i + vec2(1.5, 0.5)) * texel));
    vec2 c = decV(texture2D(v, (i + vec2(0.5, 1.5)) * texel)), d = decV(texture2D(v, (i + 1.5) * texel));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }
`;
const advectVelFS = `
  precision highp float;
  ${VEL_CHUNK}
  uniform sampler2D uVel;
  uniform vec2 uTexel;
  uniform float uDt, uDecay;
  varying vec2 vUv;
  void main() {
    vec2 v = velAt(uVel, vUv, uTexel);
    vec2 pos = clamp(vUv - v * uDt * uTexel, uTexel * 0.5, 1.0 - uTexel * 0.5);
    vec2 nv = velAt(uVel, pos, uTexel) * uDecay;
    // the borders: no flow through the glass frame
    vec2 b = step(uTexel * 1.5, vUv) * step(vUv, 1.0 - uTexel * 1.5);
    nv *= b.x * b.y;
    gl_FragColor = encV(nv);
  }
`;
// forces: up to 6 hands on the plate, the slow rotation, the oil's push on the inks
const forcesFS = `
  precision highp float;
  ${VEL_CHUNK}
  uniform sampler2D uVel, uDye;
  uniform vec2 uTexel;
  uniform float uAspect, uDt, uRot, uH;
  uniform vec4 uF[6];          // x, y (square space, height = 1, centre 0), strength (texels/s), radius
  uniform vec4 uFt[6];         // type (0 radial, 1 vortex, 2 directional), dir.x, dir.y, unused
  varying vec2 vUv;
  void main() {
    vec2 v = decV(texture2D(uVel, vUv));
    vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0);
    for (int i = 0; i < 6; i++) {
      vec4 f = uF[i];
      if (f.z == 0.0) continue;
      vec2 d = p - f.xy;
      float r2 = dot(d, d);
      float g = exp(-r2 / (f.w * f.w));
      float t = uFt[i].x;
      vec2 dir = t < 0.5 ? d / max(sqrt(r2), 1e-4) : t < 1.5 ? vec2(-d.y, d.x) / max(sqrt(r2), 1e-4) : uFt[i].yz;
      if (t < 0.5) g *= smoothstep(0.0, 0.15 * f.w, sqrt(r2));
      v += dir * f.z * g * uDt * 10.0;
    }
    // the whole plate turning, slowly, toward solid rotation
    vec2 rotV = vec2(-p.y, p.x) * uRot * uH;
    v += (rotV - v) * min(1.0, uDt * 0.12);
    gl_FragColor = encV(v);
  }
`;
const divergenceFS = `
  precision highp float;
  ${VEL_CHUNK}
  uniform sampler2D uVel;
  uniform vec2 uTexel;
  varying vec2 vUv;
  void main() {
    float l = decV(texture2D(uVel, vUv - vec2(uTexel.x, 0.0))).x, r = decV(texture2D(uVel, vUv + vec2(uTexel.x, 0.0))).x;
    float b = decV(texture2D(uVel, vUv - vec2(0.0, uTexel.y))).y, t = decV(texture2D(uVel, vUv + vec2(0.0, uTexel.y))).y;
    gl_FragColor = encS(0.5 * (r - l + t - b));
  }
`;
const jacobiFS = `
  precision highp float;
  ${VEL_CHUNK}
  uniform sampler2D uP, uDiv;
  uniform vec2 uTexel;
  varying vec2 vUv;
  void main() {
    float l = decS(texture2D(uP, vUv - vec2(uTexel.x, 0.0))), r = decS(texture2D(uP, vUv + vec2(uTexel.x, 0.0)));
    float b = decS(texture2D(uP, vUv - vec2(0.0, uTexel.y))), t = decS(texture2D(uP, vUv + vec2(0.0, uTexel.y)));
    float d = decS(texture2D(uDiv, vUv));
    gl_FragColor = encS((l + r + b + t - d) * 0.25);
  }
`;
const gradientFS = `
  precision highp float;
  ${VEL_CHUNK}
  uniform sampler2D uVel, uP;
  uniform vec2 uTexel;
  varying vec2 vUv;
  void main() {
    float l = decS(texture2D(uP, vUv - vec2(uTexel.x, 0.0))), r = decS(texture2D(uP, vUv + vec2(uTexel.x, 0.0)));
    float b = decS(texture2D(uP, vUv - vec2(0.0, uTexel.y))), t = decS(texture2D(uP, vUv + vec2(0.0, uTexel.y)));
    vec2 v = decV(texture2D(uVel, vUv)) - 0.5 * vec2(r - l, t - b);
    gl_FragColor = encV(v);
  }
`;
// the inks (rgb) and the oil (a, first target only): advected on the fine grid, with splats
const dyeFS = `
  precision highp float;
  ${VEL_CHUNK}
  uniform sampler2D uVel, uDye, uOilSrc;
  uniform vec2 uTexel, uDyeTexel;
  uniform float uAspect, uDt, uDecay, uHasOil, uOilHere, uKick;
  uniform vec2 uStage;
  uniform vec4 uSplat[2];      // x, y (square space), radius, amount
  uniform vec4 uSplatC[2];     // rgb weights (which ink channels), oil
  varying vec2 vUv;
  void main() {
    vec2 v = velAt(uVel, vUv, uTexel);
    vec2 pos0 = vUv - v * uDt * uTexel;
    if (uKick > 0.0) {
      vec2 d = (vUv - 0.5) * vec2(uAspect, 1.0) - uStage;
      float r = length(d);
      float g = exp(-r * r / (0.22 * 0.22)) * smoothstep(0.0, 0.05, r);
      pos0 -= d / max(r, 1e-4) * uKick * g * uDt * uTexel;
    }
    pos0 = clamp(pos0, uDyeTexel * 0.5, 1.0 - uDyeTexel * 0.5);
    vec2 pos = pos0;
    // the oil's gradient (fine grid): the inks are read from upstream of it, so they are
    // pushed out of the bubbles; under the oil they are erased
    vec4 cur = texture2D(uDye, vUv);
    float oil = 0.0;
    if (uHasOil > 0.5) {
      vec4 os = uOilHere > 0.5 ? cur : texture2D(uOilSrc, vUv);
      float oL = (uOilHere > 0.5 ? texture2D(uDye, vUv - vec2(uDyeTexel.x, 0.0)) : texture2D(uOilSrc, vUv - vec2(uDyeTexel.x, 0.0))).a;
      float oR = (uOilHere > 0.5 ? texture2D(uDye, vUv + vec2(uDyeTexel.x, 0.0)) : texture2D(uOilSrc, vUv + vec2(uDyeTexel.x, 0.0))).a;
      float oB = (uOilHere > 0.5 ? texture2D(uDye, vUv - vec2(0.0, uDyeTexel.y)) : texture2D(uOilSrc, vUv - vec2(0.0, uDyeTexel.y))).a;
      float oT = (uOilHere > 0.5 ? texture2D(uDye, vUv + vec2(0.0, uDyeTexel.y)) : texture2D(uOilSrc, vUv + vec2(0.0, uDyeTexel.y))).a;
      vec2 g = vec2(oR - oL, oT - oB);
      oil = os.a;
      pos += g * 1.2 * uDyeTexel * (0.5 + 2.0 * oil);
    }
    pos = clamp(pos, uDyeTexel * 0.5, 1.0 - uDyeTexel * 0.5);
    vec3 ink = texture2D(uDye, pos).rgb * uDecay;
    float o = texture2D(uDye, pos0).a;
    pos = pos0;
    if (uHasOil > 0.5 && uOilHere > 0.5) {
      // a double well keeps the oil at 0 or 1 with smooth edges; a little blur keeps it calm
      // surface tension, cheaply: a 2-texel blur then the double well; slivers thinner than the
      // blur sink under 0.5 and vanish, round blobs stay
      vec2 e2 = uDyeTexel * 2.0;
      float oS = 0.25 * (texture2D(uDye, pos - vec2(e2.x, 0.0)).a + texture2D(uDye, pos + vec2(e2.x, 0.0)).a + texture2D(uDye, pos - vec2(0.0, e2.y)).a + texture2D(uDye, pos + vec2(0.0, e2.y)).a);
      o = mix(o, oS, 0.4);
      o += uDt * 9.0 * (o - 0.5) * o * (1.0 - o);
      o = clamp(o, 0.0, 1.0);
      oil = o;
    }
    if (uHasOil > 0.5) ink *= 1.0 - 0.35 * smoothstep(0.35, 0.6, oil);
    // the splats: an ink poured, an oil bubble laid
    vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0);
    for (int i = 0; i < 2; i++) {
      vec4 sp = uSplat[i];
      if (sp.w == 0.0) continue;
      vec2 d = p - sp.xy;
      float g = exp(-dot(d, d) / (sp.z * sp.z));
      ink += uSplatC[i].rgb * sp.w * g;
      if (uHasOil > 0.5 && uOilHere > 0.5) o = max(o, uSplatC[i].a * smoothstep(0.55, 0.85, g));
    }
    gl_FragColor = vec4(clamp(ink, 0.0, 1.0), uOilHere > 0.5 ? o : 0.0);
  }
`;
// the projection: subtractive inks over a warm lamp, oil bubbles, the lens
const displayFS = `
  precision highp float;
  ${VEL_CHUNK}
  uniform sampler2D uDye1, uDye2, uVel;
  uniform vec2 uTexel;
  uniform vec3 uAbs[6];        // each ink's absorption (1 - colour)
  uniform float uAspect, uPx, uGain, uVivid, uHasOil, uShowVel, uT;
  uniform vec2 uStage, uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  vec3 inks(vec2 uv) {
    vec3 a = texture2D(uDye1, uv).rgb, b = texture2D(uDye2, uv).rgb;
    vec3 A = a.r * uAbs[0] + a.g * uAbs[1] + a.b * uAbs[2] + b.r * uAbs[3] + b.g * uAbs[4] + b.b * uAbs[5];
    // the mix stays vivid: the total absorption is capped after the vividness, so three inks
    // piled up still let some light through in every channel (no mud, no black)
    A *= uVivid;
    float m = max(max(A.r, A.g), A.b);
    A *= min(1.0, 2.2 / max(m, 1e-3));
    return exp(-A);
  }
  void main() {
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    float rr = length(s - uStage) / max(uAspect, 1.0);
    // a weak chromatic aberration at the lens edge
    vec2 dir = (vUv - 0.5) * 0.004 * smoothstep(0.3, 1.0, rr);
    vec3 T = vec3(inks(vUv + dir).r, inks(vUv).g, inks(vUv - dir).b);
    vec3 lamp = vec3(0.26, 0.235, 0.185);
    vec3 col = lamp * T;
    if (uHasOil > 0.5) {
      float o = texture2D(uDye1, vUv).a;
      float w = max(fwidth(o), 1e-4);
      float mask = smoothstep(0.5 - w, 0.5 + w, o);
      float dpx = abs(o - 0.5) / w;
      float rim = 1.0 - smoothstep(0.8, 3.2, dpx);
      // the oil: clear, a dark meniscus, an iridescent glint toward the lamp
      vec2 g = vec2(dFdx(o), dFdy(o));
      vec2 n = g / max(length(g), 1e-5);
      float glint = max(0.0, dot(n, vec2(-0.55, 0.83))) * (1.0 - smoothstep(1.5, 7.0, dpx)) * mask;
      vec3 irid = 0.5 + 0.5 * cos(6.2831853 * (dpx * 0.08 + vec3(0.0, 0.33, 0.67)));
      col = mix(col, lamp * vec3(1.04, 1.02, 0.98) + irid * glint * 0.45, mask);
      col *= 1.0 - 0.65 * rim;
    }
    // the lens: a round vignette, dark outside the projection circle
    float lens = 1.0 - smoothstep(0.92, 1.22, rr);
    col *= lens;
    if (uShowVel > 0.5) { vec2 v = velAt(uVel, vUv, uTexel) / 150.0; col = vec3(0.5 + 0.5 * v, 0.5); }
    // the cover's mask follows the cover (R19)
    vec2 qq = abs(s - uSafeCenter) - uSafeHalfSize;
    float dz = length(max(qq, 0.0)) + min(max(qq.x, qq.y), 0.0);
    col *= mix(0.45, 1.0, smoothstep(0.0, uSafeFeather, dz));
    gl_FragColor = vec4(col * uGain, 1.0);
  }
`;

export class OilshowVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIERS[o.qualityTier] ? o.qualityTier : 1;
    this._T = TIERS[this._tier];
    this._dbgInk = urlParam('oilink') === '1';
    this._dbgShake = urlParam('oilshake') === '1';
    this._showVel = urlParam('oilvel') === '1';
    this._adapt = new Adapt();
    this._post = new VdjPost({ bloomStrength: 0.12, bloomThreshold: 0.9, grain: 0.025, vignette: 0.15, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
    if (urlParam('oilprobe') === '1' && typeof window !== 'undefined') window.__oilshow = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    const T = this._T;
    this._forces = [];                                  // { x, y, type, strength, radius, until, dir }
    this._splats = [];                                  // { x, y, r, amount, c: [r,g,b,oil], target }
    this._ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this._simScene = new THREE.Scene();
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), null);
    this._quad.frustumCulled = false;
    this._simScene.add(this._quad);
    const mk = (fs, uniforms) => new THREE.ShaderMaterial({ uniforms, vertexShader: quadVS, fragmentShader: fs, depthTest: false, depthWrite: false });
    const enc = { value: 0 }, texel = { value: new THREE.Vector2(1 / T.w, 1 / T.h) }, dyeTexel = { value: new THREE.Vector2(1 / (T.w * T.dye), 1 / (T.h * T.dye)) };
    this._enc = enc; this._texel = texel; this._dyeTexel = dyeTexel;
    this._aspect = { value: 1.78 };
    this._mats = {
      advect: mk(advectVelFS, { uEnc: enc, uVel: { value: null }, uTexel: texel, uDt: { value: 0 }, uDecay: { value: 1 } }),
      forces: mk(forcesFS, { uEnc: enc, uVel: { value: null }, uDye: { value: null }, uTexel: texel, uAspect: this._aspect, uDt: { value: 0 }, uRot: { value: 0 }, uH: { value: T.h },
        uF: { value: Array.from({ length: 6 }, () => new THREE.Vector4()) }, uFt: { value: Array.from({ length: 6 }, () => new THREE.Vector4()) } }),
      div: mk(divergenceFS, { uEnc: enc, uVel: { value: null }, uTexel: texel }),
      jacobi: mk(jacobiFS, { uEnc: enc, uP: { value: null }, uDiv: { value: null }, uTexel: texel }),
      grad: mk(gradientFS, { uEnc: enc, uVel: { value: null }, uP: { value: null }, uTexel: texel }),
      dye: mk(dyeFS, { uEnc: enc, uVel: { value: null }, uDye: { value: null }, uOilSrc: { value: null }, uTexel: texel, uDyeTexel: dyeTexel, uAspect: this._aspect, uDt: { value: 0 }, uDecay: { value: 1 },
        uHasOil: { value: T.oil ? 1 : 0 }, uOilHere: { value: 1 }, uKick: { value: 0 }, uStage: safeZoneUniforms.uStageCenter, uSplat: { value: [new THREE.Vector4(), new THREE.Vector4()] }, uSplatC: { value: [new THREE.Vector4(), new THREE.Vector4()] } })
    };
    this._dispU = {
      uEnc: enc, uDye1: { value: null }, uDye2: { value: null }, uVel: { value: null }, uTexel: texel,
      uAbs: { value: INKS.map((c) => new THREE.Vector3(1 - c[0], 1 - c[1], 1 - c[2])) },
      uAspect: safeZoneUniforms.uAspect, uPx: { value: 2 / 1080 }, uGain: { value: 1 }, uVivid: { value: 2.2 }, uHasOil: { value: T.oil ? 1 : 0 }, uShowVel: { value: this._showVel ? 1 : 0 }, uT: { value: 0 },
      uStage: safeZoneUniforms.uStageCenter, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mk(displayFS, this._dispU));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);

    this._seed = 11; this._trackSeen = undefined;
    this._time = 0; this._pending = 0; this._freePhase = 0; this._beats = 0;
    this._inkIdx = 0; this._shakeUntil = -1; this._intensity = 0.3; this._kickT = -9;
    this._clock = new SceneClock({ period: SCENE_S, start: 'random' });
    this._warm = false; this._targets = null;
    this._log = [];
    this._pourTwo();
  }

  _rnd() { this._seed = (Math.imul(this._seed, 1664525) + 1013904223) >>> 0; return this._seed / 4294967296; }
  _note(e) { e.t = +this._time.toFixed(2); this._log.push(e); if (this._log.length > 30) this._log.shift(); }
  _randPos(margin) { const a = this._aspect.value; return [(this._rnd() - 0.5) * (a - 2 * margin), (this._rnd() - 0.5) * (1 - 2 * margin)]; }

  // a drop of the next ink, poured somewhere; a push as it lands
  _pour(amount) {
    const [x, y] = this._randPos(0.2);
    const k = this._inkIdx; this._inkIdx = (this._inkIdx + 1) % 6;
    const c = [0, 0, 0, 0]; c[k % 3] = 1;
    this._splats.push({ x, y, r: 0.13 + 0.06 * this._rnd(), amount: amount || 0.9, c, target: k < 3 ? 0 : 1, frames: 8 });
    this._forces.push({ x, y, type: 0, strength: 40, radius: 0.18, until: this._time + 0.25 });
    this._note({ what: 'pour', ink: k });
  }
  _pourTwo() { this._pour(0.9); this._pour(0.9); }
  _oilBubble() {
    if (!this._T.oil) return;
    const [x, y] = this._randPos(0.15);
    this._splats.push({ x, y, r: 0.07 + 0.09 * this._rnd(), amount: 1, c: [0, 0, 0, 1], target: 0, frames: 2 });
  }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    const playing = profile.playbackState === 'PLAYING';
    const a = this._adapt.update(playing ? dt : 0, profile);
    if (profile.trackId !== this._trackSeen) {
      this._trackSeen = profile.trackId;
      this._seed = hashStr(String(profile.title || '') + '|' + String(profile.artist || '')) || 11;
    }
    if (!playing) { this._pending = 0; return; }           // frozen, never black
    this._time += dt; this._pending += dt;
    this._clock.update(dt);
    this._intensity += (a.intensity - this._intensity) * Math.min(1, dt / 2);
    const I = this._intensity;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || NO_BPM;
    const beatS = 60 / bpm;
    const pulsed = a.pulse > 0.25;
    let beat = a.beat, phase;
    if (pulsed) phase = a.beatPhase;
    else { this._freePhase += dt / beatS; if (this._freePhase >= 1) { this._freePhase -= 1; beat = true; } phase = this._freePhase; }
    if (beat) this._beats++;
    const bar = a.bar || (!pulsed && beat && this._beats % 4 === 0);
    const phrase = a.phrase || (!pulsed && beat && this._beats % 16 === 0);
    const b = a.bands || { low: 0.3, mid: 0.3, high: 0.2 };
    const t = this._time;

    // the scene: two drops poured at its start (and an oil bubble)
    if (this._clock.wrapped) { this._pourTwo(); this._oilBubble(); this._oilBubble(); }
    if (bar && this._rnd() < 0.35) this._oilBubble();
    if (phrase) this._pour(0.8 + 0.3 * I);
    if (this._dbgInk && Math.floor(t / 4) !== Math.floor((t - dt) / 4)) this._pour(1);
    // the kick: a press at the centre, every beat when intense, on strong low attacks otherwise
    const kick = (beat && I > 0.7) || (a.onset > 0.75 && b.low > 0.45 && t - this._kickT > 0.3 && I >= 0.5);
    if (kick) { this._kickT = t; this._kick = 240 + 240 * I; }
    this._kick = (this._kick || 0) * Math.pow(0.5, dt / 0.06);
    // the bar: a finger flick, a vortex somewhere
    if (bar && I >= 0.5) {
      const [x, y] = this._randPos(0.25);
      this._forces.push({ x, y, type: 1, strength: (this._rnd() < 0.5 ? 1 : -1) * (120 + 100 * I), radius: 0.14, until: t + 0.18 });
    }
    // the drop: the plate shaken for one bar
    const drop = a.drop || (this._dbgShake && Math.floor(t / 8) !== Math.floor((t - dt) / 8));
    if (drop) {
      this._shakeUntil = t + 4 * beatS;
      const director = profile.director;
      if (director && this._comfort.requestFlash(t)) director.flash = VDJ_DROP_FLASH;
      this._note({ what: 'shake' });
    }
    if (t < this._shakeUntil && this._rnd() < dt * 14) {
      const [x, y] = this._randPos(0.1);
      const ang = this._rnd() * TAU;
      this._forces.push({ x, y, type: 2, strength: 260, radius: 0.18 + 0.12 * this._rnd(), until: t + 0.08, dir: [Math.cos(ang), Math.sin(ang)] });
    }
    // the slow rotation from the mids
    this._rot = (b.mid - 0.25) * 0.5 * (I < 0.5 ? 1.2 : 1);
    this._forces = this._forces.filter((f) => f.until > t);
    // the look: more vivid when intense, the RGB split on the beat only over 0.7
    this._dispU.uVivid.value = 2.2 + 0.8 * Math.max(0, (I - 0.5) / 0.5);
    // R10: the projection fills more of the frame in portrait and in wide strips
    const asp = this._aspect.value;
    this._dispU.uGain.value = Math.min(1, a.lumaCap / 0.42) * (asp < 1 ? 1.0 : asp > 2.4 ? 0.80 : 0.85);
    this._dispU.uT.value = t;
    this._post.update(1 + (vdjBeatChroma(phase, beatS) - 1) * Math.max(0, (I - 0.7) / 0.3) * 0.5);
  }

  _ensureTargets(renderer, aspect) {
    const T = this._T;
    const portrait = aspect < 1;
    const w = portrait ? T.h : T.w, h = portrait ? T.w : T.h;
    if (this._targets && this._targets.w === w) return;
    if (this._targets) for (const k of ['v0', 'v1', 'p0', 'p1', 'div', 'd0', 'd1', 'e0', 'e1']) this._targets[k].dispose();
    const ext = renderer.extensions;
    const floatOk = renderer.capabilities.isWebGL2 && (ext.has('EXT_color_buffer_float') || ext.has('EXT_color_buffer_half_float'));
    this._enc.value = floatOk ? 0 : 1;
    const simOpts = { type: floatOk ? THREE.HalfFloatType : THREE.UnsignedByteType, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, depthBuffer: false, wrapS: THREE.ClampToEdgeWrapping, wrapT: THREE.ClampToEdgeWrapping };
    const dyeOpts = { type: THREE.UnsignedByteType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, wrapS: THREE.ClampToEdgeWrapping, wrapT: THREE.ClampToEdgeWrapping };
    const mkS = () => new THREE.WebGLRenderTarget(w, h, simOpts), mkD = () => new THREE.WebGLRenderTarget(w * T.dye, h * T.dye, dyeOpts);
    this._targets = { w, h, v0: mkS(), v1: mkS(), p0: mkS(), p1: mkS(), div: mkS(), d0: mkD(), d1: mkD(), e0: mkD(), e1: mkD() };
    this._texel.value.set(1 / w, 1 / h); this._dyeTexel.value.set(1 / (w * T.dye), 1 / (h * T.dye));
    this._mats.forces.uniforms.uH.value = h;
    this._warm = false;
  }

  _pass(renderer, mat, target) {
    this._quad.material = mat;
    renderer.setRenderTarget(target);
    renderer.render(this._simScene, this._ortho);
  }

  _step(renderer, dt) {
    const G = this._targets, M = this._mats, T = this._T;
    const decay = Math.pow(0.5, dt / 2.2), dyeDecay = Math.pow(0.5, dt / 40);
    // 1. advect the velocity
    M.advect.uniforms.uVel.value = G.v0.texture; M.advect.uniforms.uDt.value = dt; M.advect.uniforms.uDecay.value = decay;
    this._pass(renderer, M.advect, G.v1);
    // 2. the hands on the plate
    const F = M.forces.uniforms;
    for (let i = 0; i < 6; i++) {
      const f = this._forces[i];
      if (f) { F.uF.value[i].set(f.x, f.y, f.strength, f.radius); F.uFt.value[i].set(f.type, f.dir ? f.dir[0] : 0, f.dir ? f.dir[1] : 0, 0); }
      else F.uF.value[i].set(0, 0, 0, 1);
    }
    F.uVel.value = G.v1.texture; F.uDt.value = dt; F.uRot.value = this._rot || 0;
    this._pass(renderer, M.forces, G.v0);
    // 3. divergence, pressure, projection
    M.div.uniforms.uVel.value = G.v0.texture;
    this._pass(renderer, M.div, G.div);
    let pr = G.p0, pw = G.p1;
    M.jacobi.uniforms.uDiv.value = G.div.texture;
    for (let i = 0; i < T.iter; i++) {
      M.jacobi.uniforms.uP.value = pr.texture;
      this._pass(renderer, M.jacobi, pw);
      const tmp = pr; pr = pw; pw = tmp;
    }
    M.grad.uniforms.uVel.value = G.v0.texture; M.grad.uniforms.uP.value = pr.texture;
    this._pass(renderer, M.grad, G.v1);
    const tv = G.v0; G.v0 = G.v1; G.v1 = tv;
    // 4. the inks and the oil, on the fine grid
    const D = M.dye.uniforms;
    D.uVel.value = G.v0.texture; D.uDt.value = dt; D.uDecay.value = dyeDecay; D.uKick.value = this._kick || 0;
    for (let k = 0; k < 2; k++) {
      const s = this._splats.filter((sp) => sp.target === k).slice(0, 2);
      for (let i = 0; i < 2; i++) {
        const sp = s[i];
        if (sp) { D.uSplat.value[i].set(sp.x, sp.y, sp.r, sp.amount * dt * 14); D.uSplatC.value[i].set(sp.c[0], sp.c[1], sp.c[2], sp.c[3]); }
        else D.uSplat.value[i].set(0, 0, 1, 0);
      }
      D.uDye.value = (k === 0 ? G.d0 : G.e0).texture; D.uOilSrc.value = G.d0.texture; D.uOilHere.value = k === 0 ? 1 : 0;
      this._pass(renderer, M.dye, k === 0 ? G.d1 : G.e1);
    }
    for (const sp of this._splats) sp.frames--;
    this._splats = this._splats.filter((sp) => sp.frames > 0);
    const td = G.d0; G.d0 = G.d1; G.d1 = td;
    const te = G.e0; G.e0 = G.e1; G.e1 = te;
  }

  _render(renderer) {
    const cur = renderer.getRenderTarget();
    let w, h;
    if (cur) { w = cur.width; h = cur.height; } else { const b = renderer.getDrawingBufferSize(_size); w = b.x; h = b.y; }
    this._aspect.value = w / h;
    this._dispU.uPx.value = 2 / h;
    this._ensureTargets(renderer, w / h);
    const cc = renderer.getClearColor(_cc), ca = renderer.getClearAlpha();
    const xr = renderer.xr.enabled; renderer.xr.enabled = false;
    if (!this._warm) {
      // the plate is already alive at the first frame: a few bubbles, two drops, a stir, 80 steps
      this._warm = true;
      // alpha 0: the oil lives in the first dye target's alpha (cleared to 1, the whole plate is oil)
      renderer.setClearColor(0x000000, 0);
      for (const k of ['v0', 'v1', 'p0', 'p1', 'div', 'd0', 'd1', 'e0', 'e1']) { renderer.setRenderTarget(this._targets[k]); renderer.clear(true, false, false); }
      this._splats = [];
      const [x, y] = this._randPos(0.25);
      this._forces.push({ x, y, type: 1, strength: 90, radius: 0.24, until: this._time + 0.5 });
      for (let i = 0; i < 80; i++) {
        if (i % 20 === 0 && i < 80) this._oilBubble();
        if (i === 30) this._pourTwo();
        this._step(renderer, 1 / 60);
        this._forces = this._forces.filter((f) => f.until > this._time + (i - 30) / 60);
      }
      this._pending = 0;
    }
    // the simulation advances by the real elapsed time, in steps of at most 1/30 s
    let n = 0;
    while (this._pending > 1e-4 && n < 3) { const dt = Math.min(this._pending, 1 / 30); this._step(renderer, dt); this._pending -= dt; n++; }
    this._pending = 0;
    renderer.setRenderTarget(cur);
    renderer.setClearColor(cc, ca);
    renderer.xr.enabled = xr;
    const G = this._targets;
    this._dispU.uDye1.value = G.d0.texture; this._dispU.uDye2.value = G.e0.texture; this._dispU.uVel.value = G.v0.texture;
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); }
    if (this._display) { this._display.geometry.dispose(); this._display.material.dispose(); }
    if (this._quad) this._quad.geometry.dispose();
    if (this._mats) for (const k in this._mats) this._mats[k].dispose();
    if (this._targets) for (const k of ['v0', 'v1', 'p0', 'p1', 'div', 'd0', 'd1', 'e0', 'e1']) this._targets[k].dispose();
    if (this._adapt) this._adapt.dispose();
    if (typeof window !== 'undefined' && window.__oilshow === this) window.__oilshow = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _cc = new THREE.Color();
